'use client';
import { getResourceData } from '@/api/public';
import {
  checkCartBeforeCheckout,
  getFreeShippingPolicy,
  getSubTotalPrice,
  refreshCarts,
} from '@/api/cart';
import { getOrderCoupon } from '@/api/pay';
import { fetchBffDetailCouponTips } from '@/api/product';
import { ICartGroup, ICartItem } from '@/api/types/cart';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { toast } from '@/components/Toast';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { getLocal, getSetLocalStorage, setLocal, setSession } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CartEmpty } from './components/CartEmpty';
import CartItemDialog from './components/cartItemComponents/CartItemDialog';
import { CartPageWAP } from './components/CartPage.m';
import { CartPagePC } from './components/CartPage.pc';
import HalfPriceDialog from './components/HalfPriceDialog';
import { YouMightLikeWaterFall } from './components/YouMightLikeWaterFall';
import { useCartActions } from './hooks/useCartActions';
import { useCartDialogs } from './hooks/useCartDialogs';
import { useCartGaTracking } from './hooks/useCartGaTracking';
import { useCartSensorsTracking } from './hooks/useCartSensorsTracking';
import { useCartSnapshot } from './hooks/useCartSnapshot';
import { useCartState } from './hooks/useCartState';
import { useCartSwiperManager } from './hooks/useCartSwiperManager';
import { useCartThirdPartyTracking } from './hooks/useCartThirdPartyTracking';

export default function CartPage() {
  const t = useTranslations('cart.page');
  const router = useRouter();
  const { getFromRouterInfo } = useRouteInfo();
  // 使用项目标准的设备类型获取方式
  const { deviceType } = useContext(ResponsiveContext);

  // 使用弹窗管理 hook
  const cartDialogs = useCartDialogs();

  // 使用状态管理 hook
  const cartState = useCartState();

  // 使用神策埋点 hook
  const {
    trackCartView,
    trackCartLeave,
    trackCheckoutClickForThirdParty,
    trackCheckoutResult,
    trackCheckoutProductDetailResult,
    trackCartPageBannerClick,
    trackCartPageBannerExp,
    trackCartItemBtnClick,
  } = useCartSensorsTracking();

  // 使用GA埋点 hook
  const { trackConversion, trackAddCart } = useCartGaTracking();

  // 使用第三方埋点 hook
  const { trackCriteoViewBasket, trackUetqCart, trackEmarsysCart } = useCartThirdPartyTracking();

  // 使用购物车快照管理 hook
  const { initializeCartSnapshot, addToCartSnapshot, cartSnapshot } = useCartSnapshot();

  // 使用Swiper管理 hook
  const swiperManager = useCartSwiperManager();

  // 从store获取数据和方法
  const setCart = useStore((state) => state.setCart);
  // const setCanBuyCartsList = useStore((state) => state.setCanBuyCartsList);
  const canBuyList = useStore((state) => state.canBuyList); // 可结算购物车列表
  const canNotBuyList = useStore((state) => state.canNotBuyList);
  const canBuyCartsList = useStore((state) => state.canBuyCartsList); //  // 可结算购物车条目列表（可勾选的）
  const setSelectedCartIds = useStore((state) => state.setSelectedCartIds);
  const currency = useStore((state) => state.currency);
  const publicResourceData = useStore((state) => state.publicResourceData);
  const token = useStore((state) => state.token);
  const setQuickCouponDialog = useStore((state) => state.setQuickCouponDialog);
  const setCannotCheckoutData = useStore((state) => state.setCannotCheckoutData);
  const setCannotSubmitData = useStore((state) => state.setCannotSubmitData);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  // 兑换券相关
  const setSpecialCart = useStore((state) => state.setSpecialCart);
  // 购物车点击结算触发登录弹窗，如果直接跳转到payment页面，则不需要弹券弹窗
  const setNeedFastRegisterCoupon = useStore((state) => state.setNeedFastRegisterCoupon);

  // 直接从 publicResourceData 获取配置，避免单独的 state
  const halfPriceConfig =
    (publicResourceData?.config_names as any)?.product_content_half_price_member || {};

  // 状态管理现在由 useCartState hook 处理
  const {
    cartCanBuyList,
    setCartCanBuyList,
    cartCanNotBuyList,
    setCartCanNotBuyList,
    allCheck,
    setAllCheck,
    isInitData,
    setIsInitData,
    showMoreInvalid,
    setShowMoreInvalid,
    cartDefaultCheckAll,
    totalData,
    setTotalData,
    promotion,
    setPromotion,
    selectedCanBuy,
    allQty,
    handleInvalidSelectChange: cartStateHandleInvalidSelectChange,
  } = cartState;

  const getItemPromotion = useCallback(
    (cartId: number) => {
      return promotion.find((item) => item.id === cartId) || null;
    },
    [promotion],
  );

  /** 延迟埋点（如 setTimeout）需读最新计价结果，避免闭包拿到旧的 promotion / 列表 */
  const promotionRef = useRef(promotion);
  promotionRef.current = promotion;
  const cartCanBuyListRef = useRef(cartCanBuyList);
  cartCanBuyListRef.current = cartCanBuyList;

  const [isFirstIn, setIsFirstIn] = useState(true); // 判断是不是首次进入，防止页面强刷时触发登录逻辑
  const [hasProcessedLogin, setHasProcessedLogin] = useState(false); // 防止重复处理登录逻辑
  const [recommendKey, setRecommendKey] = useState(0); // 推荐组件的key，用于强制重新渲染
  const reloadCartRef = useRef<(() => Promise<void>) | null>(null); // 用于保存最新的 reloadCart 函数

  // 记录token的初始状态，用于区分真正的登录变化和页面刷新时的初始读取
  const initialTokenRef = useRef<string | null>(null);
  const [hasTokenInitialized, setHasTokenInitialized] = useState(false);
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);

  // Checkout登录相关状态管理
  const [clickCheckOut, setClickCheckOut] = useState(false); // 是否通过checkout触发登录
  const [checkoutLoading, setCheckoutLoading] = useState(false); // checkout按钮加载状态;
  /** 计价是否已至少完成一轮（get_cart_price 落盘），Cart_View 需在此之后再上报 base_checked_* */
  const [hasPricingSettled, setHasPricingSettled] = useState(false);
  const [prelandingCartList, setPrelandingCartList] = useState<ICartGroup[]>([]); // 登录前购物车列表
  const [prelandingSelectedCanBuy, setPrelandingSelectedCanBuy] = useState<ICartItem[]>([]); // 登录前选中商品

  // 记录最近修改的商品ID集合
  const [recentlyModifiedItems, setRecentlyModifiedItems] = useState<Set<number>>(new Set());

  // 初始化状态管理 - 简化为单一状态控制
  type InitializationState = 'initializing' | 'processing' | 'completed';
  const [initState, setInitState] = useState<InitializationState>('initializing');

  // 便于理解的状态检查函数
  const isCompleted = initState === 'completed';

  // 广告位数据
  const [advertData, setAdvertData] = useState({
    cart_settlement_pc_image: '',
    cart_settlement_wap_image: '',
    cart_settlement_jump_target_type: '',
    cart_settlement_jump_target: '',
    cart_settlement_product_group: {},
    cart_settlement_sku: {},
  });

  // 推荐商品数据
  const [recommendList, setRecommendList] = useState<any>({
    cart_rec_frame_group_id: '',
    waterfall_list: [],
  });

  // 包邮数据
  const [freeShipData, setFreeShipData] = useState<any>({});

  // 优惠券相关状态
  const [couponInfo, setCouponInfo] = useState<any>(null);
  const [couponData, setCouponData] = useState<any>({});

  // 计算是否显示广告图
  const showAdsImg = useMemo(() => {
    // 购物车可结算不为空，并且配置的有广告图
    if (
      cartCanBuyList.length &&
      deviceType === EPlatform.WEB &&
      advertData.cart_settlement_pc_image
    ) {
      return true;
    }
    if (
      cartCanBuyList.length &&
      deviceType === EPlatform.WAP &&
      advertData.cart_settlement_wap_image
    ) {
      return true;
    }
    return false;
  }, [cartCanBuyList.length, deviceType, advertData]);

  // 广告位跳转对象
  const advertObj = useMemo(() => {
    const imageUrl =
      deviceType === EPlatform.WEB
        ? advertData.cart_settlement_pc_image
        : advertData.cart_settlement_wap_image;

    if (
      !imageUrl ||
      !advertData.cart_settlement_jump_target_type ||
      !advertData.cart_settlement_jump_target
    ) {
      return false;
    }

    const params = {
      jump_type: Number(advertData.cart_settlement_jump_target_type),
      Jump_target: advertData.cart_settlement_jump_target,
      product_group: advertData.cart_settlement_product_group,
      sku: advertData.cart_settlement_sku,
      crowd_recommend: {},
    };
    return params;
  }, [deviceType, advertData]);

  // 设置默认数据，不调用接口
  const initializeDefaultData = useCallback(() => {
    // 设置默认的广告位数据
    setAdvertData({
      cart_settlement_pc_image: '',
      cart_settlement_wap_image: '',
      cart_settlement_jump_target_type: '',
      cart_settlement_jump_target: '',
      cart_settlement_product_group: {},
      cart_settlement_sku: {},
    });

    // 设置默认的推荐数据
    setRecommendList({
      cart_rec_frame_group_id: '',
      waterfall_list: [],
    });
  }, []);

  // 刷新数据，检查过期商品
  const refreshCartData = useCallback(async () => {
    try {
      const response = await refreshCarts();
      if (response?.data?.refresh_info && response.data.refresh_info.length > 0) {
        // 有过期商品，显示弹窗
        let num = 0;
        response.data.refresh_info.forEach((item: any) => {
          num += item.products?.length || 0;
        });
        setCannotCheckoutData({
          title: num > 1 ? t('followingProductsExpired') : t('followingProductExpired'),
          list: response.data.refresh_info,
          show: true,
          confirmBtnText: t('ok'),
          onClose: () => {
            reloadCart();
          },
        });
      } else {
        // 没有过期商品，关闭弹窗
        setCannotCheckoutData({
          show: false,
          list: [],
          onClose: () => {},
          confirmBtnText: 'OK',
        });
        // 获取兑换券数据
        try {
          await setSpecialCart();
        } catch (error) {
          logger.error('兑换券数据刷新失败:', error);
        }
      }
    } catch (error) {
      logger.error('Failed to refresh cart data:', error);
    }
  }, [setSpecialCart]);

  // 刷新购物车
  const reloadCart = useCallback(
    async (cartData?: any, modifiedCartId?: number) => {
      try {
        setIsGlobalLoading(true);

        // 如果指定了修改的商品ID，记录到集合中
        if (modifiedCartId) {
          setRecentlyModifiedItems((prev) => new Set(Array.from(prev).concat(modifiedCartId)));
        }

        if (cartData) {
          // 如果传入了购物车数据，直接使用，避免重复调用API
          await setCart(cartData);
        } else {
          // 没有传入数据时，调用API获取最新数据
          await setCart();
        }
        setIsGlobalLoading(false);
        await refreshCartData();
      } catch (error) {
        logger.error('Failed to reload cart data:', error);
      }
    },
    [setCart, refreshCartData],
  );

  // 更新 reloadCart ref
  reloadCartRef.current = reloadCart; // 直接赋值，无需useEffect

  // 计算选中商品的ID字符串
  const selectedCartIdStr = selectedCanBuy.map((item: any) => item.cart_id).join(',');

  // 调试：记录 selectedCanBuy 的变化
  const selectedCanBuyChangeRef = useRef<string>('');
  const currentSelectedCanBuyStr = JSON.stringify(
    selectedCanBuy.map((item) => ({ cart_id: item.cart_id, selected: true })),
  );
  if (selectedCanBuyChangeRef.current !== currentSelectedCanBuyStr) {
    selectedCanBuyChangeRef.current = currentSelectedCanBuyStr;
  }

  // 计算选中商品数据变化的标识符，用于触发价格重新计价
  // 只监听选中商品的数据变化，包括option中的任意参数变化
  const selectedCartDataHash = useMemo(() => {
    return selectedCanBuy
      .map((item) => {
        // 构建包含所有影响价格字段的标识符
        const baseInfo = `${item.cart_id}-${item.product_id}-${item.qty}`;
        // 监听整个option对象的变化，包括处方、镜片、镀膜等所有参数
        const optionInfo = JSON.stringify(item.option || {});
        return `${baseInfo}-${optionInfo}`;
      })
      .join(',');
  }, [selectedCanBuy]);

  // 计算可结算商品的cart_id数组
  const allSelectedCartIds = useMemo(() => {
    return selectedCanBuy.map((item: any) => item.cart_id).filter(Boolean);
  }, [selectedCanBuy]);

  // 使用批量操作 hook
  const cartActions = useCartActions({
    cartCanBuyList,
    cartCanNotBuyList,
    setShowCartItemDialog: cartDialogs.setShowCartItemDialog,
    setCartItemDialogProps: cartDialogs.setCartItemDialogProps,
    reloadCart,
    setCartCanBuyList,
    setCartCanNotBuyList,
    setAllCheck,
    token,
    setShowLoginRegisterDialog,
    getItemPromotion,
  });

  // 监听selectedCanBuy变化，自动保存到本地存储 (对应Vue的selectedCanBuy watch)
  const selectedCanBuyRef = useRef(selectedCanBuy);
  selectedCanBuyRef.current = selectedCanBuy;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // 只有在完全初始化完成后的用户操作才保存到localStorage
      // 避免初始化期间的空数组覆盖用户之前的选择
      if (isCompleted && cartCanBuyList && cartCanBuyList.length > 0) {
        // 反向存储：只记录未勾选的商品ID
        const unselectedIds = cartCanBuyList
          .filter((item) => !item.selected && !item.disabled)
          .map((item) => item.carts?.[0]?.cart_id)
          .filter(Boolean);
        setLocal('zl-uncheckedCart', unselectedIds);
        updateEnterTime(); // 使用正确的过期时间设置
      }
    }
  }, [selectedCanBuy, cartCanBuyList, isCompleted]); // 监听cartCanBuyList变化

  // 将状态更新移到useEffect中，避免在渲染期间调用setState。 学渊添加的，影响了勾选逻辑，先注释掉。
  // useEffect(() => {
  //   setCanBuyCartsList(cartCanBuyList);
  // }, [cartCanBuyList, setCanBuyCartsList]);

  // 更新进入购物车的时间 (对应Vue的updateEnterTime)
  const updateEnterTime = useCallback(() => {
    try {
      const date = new Date().getTime();
      let time = 5; // 开发环境默认缓存时间是5分钟
      if (process.env.NODE_ENV === 'production') {
        time = 30; // 正式环境缓存时间为30分钟
      }
      getSetLocalStorage.set('zl-hasEnterCart', 1, date + time * 60 * 1000); // 30分钟内有效
    } catch (error) {
      logger.error('Failed to update enter time:', error);
    }
  }, []);

  // 检查登陆前后商品列表是否匹配 (对应Vue的listEqual)
  const listEqual = useCallback((listNew: ICartItem[], listOld: ICartItem[]): boolean => {
    try {
      // 如果数组长度不相等，则两个数组一定不相等
      if (listNew.length !== listOld.length) {
        return false;
      }

      for (let i = 0; i < listNew.length; i++) {
        let found = false;
        for (let j = 0; j < listOld.length; j++) {
          // 登陆后的visitor_cart_id === 登陆前的cart_id 且数量相同
          if (
            (listNew[i] as any).visitor_cart_id === listOld[j].cart_id &&
            listNew[i].qty === listOld[j].qty
          ) {
            found = true;
            break;
          }
        }
        if (!found) {
          return false;
        }
      }
      // 如果以上条件都不满足，则可以匹配
      return true;
    } catch (error) {
      logger.error('Failed to compare cart lists:', error);
      return false;
    }
  }, []);

  // checkout验证和跳转逻辑
  const checkoutVerify = useCallback(
    async (cart_ids: number[]) => {
      try {
        // 1. 检查活动是否过期
        // 检查过期商品
        const responseData = await refreshCarts();
        if (responseData?.data?.refresh_info && responseData.data.refresh_info.length > 0) {
          // 有过期商品，显示弹窗
          let num = 0;
          responseData.data.refresh_info.forEach((item: any) => {
            num += item.products?.length || 0;
          });
          setCannotCheckoutData({
            title: num > 1 ? t('followingProductsExpired') : t('followingProductExpired'),
            list: responseData.data.refresh_info,
            show: true,
            confirmBtnText: t('ok'),
            onClose: () => {
              reloadCart();
            },
          });
          return;
        }

        // 2. 验证金额是否为0（赠品检查）
        if (totalData.subtotal === '0.00') {
          let allow = true;
          for (let i = 0; i < prelandingSelectedCanBuy.length; i++) {
            if (prelandingSelectedCanBuy[i].type !== 4 && prelandingSelectedCanBuy[i].type !== 5) {
              allow = false;
              break;
            }
          }
          if (!allow) {
            toast.error(t('subtotalZeroError'));
            return;
          }
        }

        // 3. 校验商品限购、售罄等
        setCheckoutLoading(true);
        const response = await checkCartBeforeCheckout({ cart_ids: cart_ids.map(String) });
        setCheckoutLoading(false);

        const { data, code } = response as any;
        if (code === 200 && data.code) {
          // 有限制，不能跳转结算页
          handleErrorCode(data);
          return;
        }
        if (code !== 200) {
          return;
        }
        setSelectedCartIds(cart_ids); // 设置store中的selectedCartIds，用于payment页商品展示
        router.push('/payment');
      } catch (error) {
        logger.error('Checkout verification failed:', error);
      } finally {
        setCheckoutLoading(false);
      }
    },
    [prelandingSelectedCanBuy, totalData.subtotal, router],
  );

  // 点checout快捷登录成功之后，相关处理逻辑
  const checkoutSuccess = useCallback(async () => {
    try {
      setCheckoutLoading(true);
      const { canBuyList: currentCanBuyList } = await setCart(); // 重新获取购物车数据

      // 收集登录前后的商品列表
      const listNew: ICartItem[] = [];
      const listOld: ICartItem[] = [];

      currentCanBuyList.forEach((item) => {
        if (item.carts) {
          item.carts.forEach((cartItem) => {
            listNew.push(cartItem);
          });
        }
      });

      prelandingCartList.forEach((item) => {
        if (item.carts) {
          item.carts.forEach((cartItem) => {
            listOld.push(cartItem);
          });
        }
      });
      const isEqual = listEqual(listNew, listOld);
      if (isEqual) {
        // 购物车相同 -> 保持之前勾选状态，直接验证跳转支付
        const cart_ids: number[] = [];

        // 根据visitor_cart_id匹配，获取新的cart_id
        prelandingSelectedCanBuy.forEach((item) => {
          listNew.forEach((items) => {
            if (item.cart_id === (items as any).visitor_cart_id) {
              cart_ids.push(items.cart_id);
            }
          });
        });

        // 关键逻辑：登录前勾选的商品，登录之后需要仅勾选上登录前勾选的商品
        const cartCanBuyArr = JSON.parse(JSON.stringify(cartCanBuyList)) as ICartGroup[];
        cartCanBuyArr.forEach((item) => {
          // 把之前勾选过的内容勾选上
          if (cart_ids.includes(item.carts?.[0]?.cart_id)) {
            // 如果是之前选中的商品
            item.selected = true; // 勾选可结算的商品
          } else {
            item.selected = false;
          }
        });
        setCartCanBuyList(cartCanBuyArr);

        // 直接验证并跳转支付
        await checkoutVerify(cart_ids);
      } else {
        // 购物车不同 -> 按正常流程处理
        await refreshCarts();
      }
    } catch (error) {
      logger.error('Checkout success handling failed:', error);
    } finally {
      setCheckoutLoading(false);
    }
  }, [cartCanBuyList, prelandingCartList, prelandingSelectedCanBuy, listEqual, checkoutVerify]);

  // 统一的勾选状态处理函数 (合并handleMountedCheck和Store监听的勾选逻辑)
  const processCartSelectionState = useCallback(
    (
      sourceCanBuyList: ICartGroup[],
      sourceCanBuyCartsList: ICartGroup[],
      options: {
        isInitialization?: boolean; // 是否为初始化调用
        currentBatchFlag?: boolean; // 当前的批量模式状态
        recentlyModifiedItems?: Set<number>; // 最近修改的商品ID集合
        providedSnapshot?: Set<number>; // 提供的快照数据，避免依赖异步状态更新
      } = {},
    ) => {
      const {
        isInitialization = false,
        currentBatchFlag = false,
        recentlyModifiedItems: modifiedItems = new Set(),
        providedSnapshot,
      } = options;
      const list = JSON.parse(JSON.stringify(sourceCanBuyList)) as ICartGroup[]; // 深拷贝

      let hasEnter = 0;
      let uncheckedCart: number[] = [];

      try {
        const storedUncheckedCart = getLocal('zl-uncheckedCart');
        uncheckedCart = Array.isArray(storedUncheckedCart) ? (storedUncheckedCart as number[]) : [];
        hasEnter = getSetLocalStorage.get('zl-hasEnterCart') || 0; // 使用带过期检查的读取
      } catch (e) {
        logger.error('Failed to get localStorage data:', e);
      }
      // 收集新添加的商品ID，用于更新快照
      const newCartIds: number[] = [];

      list.forEach((item: any) => {
        // 在canBuyCartsList查找list的购物车id，如果存在，则表示该条目可结算
        const findCanBuy = sourceCanBuyCartsList.find(
          (cartItem: any) => item.carts?.[0]?.cart_id === cartItem.carts?.[0]?.cart_id,
        );

        if (findCanBuy) {
          const cartId = item.carts?.[0]?.cart_id;

          // 设置禁用状态
          if (currentBatchFlag) {
            // 批量模式下：所有商品都可勾选，包括赠品

            item.disabled = false;
          } else {
            // 正常模式下：未生效的赠品不可勾选

            item.disabled = item.type === 4 && item.is_allow_settlement === 2;
          }
          // 勾选状态逻辑
          if (hasEnter) {
            // 非首次进入 -> 检查商品状态
            // 使用提供的快照或当前快照来识别新商品
            const currentSnapshot = providedSnapshot || cartSnapshot;
            const isNewItemFlag = !currentSnapshot.has(cartId);

            if (!isNewItemFlag) {
              // 是老商品，先检查是否被修改过
              const wasRecentlyModified = modifiedItems.has(cartId);

              if (wasRecentlyModified && cartDefaultCheckAll === 1) {
                // 商品被修改且默认勾选开启：自动勾选（除非禁用）

                item.selected = !item.disabled;
              } else if (uncheckedCart.includes(cartId)) {
                // 在未勾选列表中的商品 -> 用户之前取消勾选的，保持不勾选

                item.selected = false;
              } else {
                item.selected = !item.disabled;
              }
            } else if (isInitialization && hasEnter > 0) {
              // 是新添加的商品 - 初始化时但非首次进入：优先考虑localStorage中的uncheckedCart
              if (uncheckedCart.includes(cartId)) {
                item.selected = false;
              } else {
                // 不在未勾选列表中，根据配置决定默认状态
                const shouldCheckAll = cartDefaultCheckAll === 1;

                item.selected = shouldCheckAll && !item.disabled;
              }
              // 记录新商品，用于更新快照
              newCartIds.push(cartId);
            } else {
              // 真正的新添加商品- 初始化是首次进入，根据配置决定默认状态
              const shouldCheckAll = cartDefaultCheckAll === 1;

              item.selected = shouldCheckAll && !item.disabled;
              // 记录新商品，用于更新快照
              newCartIds.push(cartId);
            }
          } else {
            // 首次进入 -> 根据配置决定默认状态
            const shouldCheckAll = cartDefaultCheckAll === 1;
            if (shouldCheckAll) {
              // 配置为默认全选：所有商品都勾选

              item.selected = !item.disabled;
            } else {
              // 配置为默认不全选：所有商品都不勾选

              item.selected = false;
            }
          }

          // 未生效的赠品在正常模式下强制设为不勾选，批量模式下可勾选
          if (!currentBatchFlag && item.type === 4 && item.is_allow_settlement === 2) {
            item.selected = false;
          }
        } else {
          // 不在可结算列表中的商品

          item.disabled = true;

          item.selected = false;
        }
      });

      // 如果有新商品，更新快照
      if (newCartIds.length > 0) {
        addToCartSnapshot(newCartIds);
      }

      // 收集已处理的修改商品ID
      const processedModifiedItems = new Set<number>();
      list.forEach((item) => {
        const cartId = item.carts?.[0]?.cart_id;
        if (cartId && modifiedItems.has(cartId)) {
          processedModifiedItems.add(cartId);
        }
      });

      return { list, processedModifiedItems };
    },
    [canBuyCartsList, cartDefaultCheckAll, publicResourceData, cartSnapshot],
  );

  // 页面初始化的勾选状态处理 (简化版，复用统一处理函数)
  const handleMountedCheck = useCallback(
    (
      currentCanBuyList?: ICartGroup[],
      currentCanBuyCartsList?: ICartGroup[],
      providedSnapshot?: Set<number>,
    ) => {
      // 使用传入的参数或者当前state，确保获取到最新的数据
      const sourceCanBuyList = currentCanBuyList || canBuyList;
      const sourceCanBuyCartsList = currentCanBuyCartsList || canBuyCartsList;

      const result = processCartSelectionState(sourceCanBuyList, sourceCanBuyCartsList, {
        isInitialization: true,
        currentBatchFlag: false, // 初始化时批量模式肯定是false
        providedSnapshot, // 传递快照数据，避免依赖异步状态更新
      });
      return result.list;
    },
    [canBuyList, canBuyCartsList, processCartSelectionState],
  );

  // 监听Store数据变化，更新本地UI状态 (对应Vue的canBuyList watch)
  const prevCanBuyListRef = useRef<string>('');
  useEffect(() => {
    // 在整个初始化周期内都不执行（包括首次进入的整个流程）
    if (!isCompleted) {
      return;
    }
    // 检查数据是否真正发生了变化
    const currentDataStr = JSON.stringify(canBuyList);
    if (prevCanBuyListRef.current === currentDataStr) {
      return;
    }
    prevCanBuyListRef.current = currentDataStr;

    // 使用统一的勾选状态处理函数
    const result = processCartSelectionState(canBuyList, canBuyCartsList, {
      isInitialization: false,
      currentBatchFlag: cartActions.batchFlag, // 传递当前的批量模式状态
      recentlyModifiedItems, // 传递修改的商品记录
    });

    setCartCanBuyList(result.list);

    // 清除已处理的修改记录
    if (result.processedModifiedItems.size > 0) {
      setRecentlyModifiedItems((prev) => {
        const newSet = new Set(prev);
        result.processedModifiedItems.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }
  }, [
    canBuyList,
    initState,
    canBuyCartsList,
    cartDefaultCheckAll,
    cartActions.batchFlag,
    processCartSelectionState,
    recentlyModifiedItems,
  ]);

  // 处理不可结算商品列表变化 (对应Vue的canNotBuyList watch)
  useEffect(() => {
    const list = JSON.parse(JSON.stringify(canNotBuyList)) as ICartItem[];
    setCartCanNotBuyList(list);
  }, [canNotBuyList]);

  // 监听cartCanBuyList和cartCanNotBuyList变化，更新全选状态 (对应Vue的cartCanBuyList/cartCanNotBuyList watch)
  useEffect(() => {
    // 防止在初始化时触发此逻辑
    if (!isCompleted) {
      return;
    }

    // 直接在useEffect内部实现逻辑，避免函数依赖导致的循环
    if (cartActions.batchFlag) {
      // 批量模式: 考虑可结算+不可结算商品
      const allCanBuySelected = !cartCanBuyList.some(
        (item) => item.selected === false && item.disabled === false,
      );
      const allCanNotBuySelected = !cartCanNotBuyList.some((item) => item.selected === false);

      const newAllCheck = allCanBuySelected && allCanNotBuySelected;

      // 可结算列表中可勾选的条目为空，下方的all不勾选
      const canCheckList = cartCanBuyList.filter((item) => item.disabled === false);
      const shouldSetAllCheck = canCheckList.length === 0 ? false : newAllCheck;

      setAllCheck((prev) => (prev !== shouldSetAllCheck ? shouldSetAllCheck : prev));
    } else {
      // 正常模式: 只考虑可结算商品
      const allSelected = !cartCanBuyList.some(
        (item) => item.selected === false && item.disabled === false,
      );

      // 可结算列表中可勾选的条目为空，下方的all不勾选
      const canCheckList = cartCanBuyList.filter((item) => item.disabled === false);
      const shouldSetAllCheck = canCheckList.length === 0 ? false : allSelected;

      setAllCheck((prev) => (prev !== shouldSetAllCheck ? shouldSetAllCheck : prev));
    }

    // 如果购物车为空，退出批量模式
    if (!cartCanBuyList.length && cartActions.batchFlag) {
      cartActions.setBatchFlag(false);
    }
  }, [cartCanBuyList, cartCanNotBuyList, cartActions.batchFlag, initState]);

  // 批量模式切换处理 (对应Vue的batchFlag watch)
  const prevBatchFlagRef = useRef(cartActions.batchFlag);
  useEffect(() => {
    // 只在batchFlag真正变化时执行，避免初始化时的不必要执行
    if (prevBatchFlagRef.current === cartActions.batchFlag || !isCompleted) {
      prevBatchFlagRef.current = cartActions.batchFlag;
      return;
    }
    prevBatchFlagRef.current = cartActions.batchFlag;

    if (cartActions.batchFlag) {
      // 进入批量模式: 所有商品都可勾选，但兑换品除外
      setCartCanBuyList((prev) =>
        prev.map((item) => {
          // 兑换品特殊处理：禁用且取消勾选
          if (item.type === 5) {
            return {
              ...item,
              disabled: true,
              selected: false,
            };
          }
          // 其他商品都启用，保持selected状态不变
          return {
            ...item,
            disabled: false,
          };
        }),
      );
    } else {
      // 退出批量模式: 恢复正常限制，保持勾选状态
      setCartCanBuyList((prev) =>
        prev.map((item) => {
          // 计算正常模式下的禁用状态
          let shouldDisable = false;

          if (item.type === 4 && item.is_allow_settlement === 2) {
            // 未生效的赠品在正常模式下禁用
            shouldDisable = true;
          } else {
            // 其他商品根据是否在可结算列表中决定禁用状态
            const findCanBuy = canBuyCartsList.find(
              (cartItem: any) => item.carts?.[0]?.cart_id === cartItem.carts?.[0]?.cart_id,
            );
            shouldDisable = !findCanBuy;
          }

          // 如果商品被禁用了，则取消勾选；否则保持原勾选状态
          const newSelected = shouldDisable ? false : item.selected;

          return {
            ...item,
            disabled: shouldDisable,
            selected: newSelected,
          };
        }),
      );
    }
  }, [cartActions.batchFlag, canBuyCartsList, initState]);

  // 全选/反选 (对应Vue的checkChange)
  const checkChange = useCallback(() => {
    const newAllCheck = !allCheck;
    const { batchFlag } = cartActions;

    const canBuyPrev = cartCanBuyListRef.current;

    /** 非编辑态全选/反选：每个勾选状态发生变化的条目各上报一次（编辑态勾选不上报） */
    const bulkCheckTracking: Array<{ cart: ICartItem; nextSelected: boolean }> = [];
    if (!batchFlag) {
      canBuyPrev.forEach((group) => {
        const nextSelected = newAllCheck && !group.disabled;
        if (group.selected !== nextSelected) {
          const cart = group.carts?.[0];
          if (cart) bulkCheckTracking.push({ cart, nextSelected });
        }
      });
    }

    setAllCheck(newAllCheck);

    if (batchFlag) {
      // 编辑模式下的全选/反选：所有商品都可勾选
      setCartCanBuyList((prev) =>
        prev.map((item) => ({
          ...item,
          selected: newAllCheck && !item.disabled,
        })),
      );
      setCartCanNotBuyList((prev) =>
        prev.map((item) => ({
          ...item,
          selected: newAllCheck,
        })),
      );
    } else {
      // 正常模式下的全选/反选
      setCartCanBuyList((prev) =>
        prev.map((item) => ({
          ...item,
          selected: newAllCheck && !item.disabled,
        })),
      );
    }

    if (bulkCheckTracking.length > 0) {
      setTimeout(() => {
        bulkCheckTracking.forEach(({ cart, nextSelected }) => {
          const cartId = cart.cart_id ?? 0;
          const btnName = nextSelected ? 'item选择' : 'item取消';
          trackCartItemBtnClick(cart as any, btnName, {
            selected: nextSelected,
            itemPromotion: promotionRef.current.find((item) => item.id === cartId) || null,
          });
        });
      }, 2000);
    }
  }, [allCheck, cartActions.batchFlag, trackCartItemBtnClick]);

  // 单项选择
  const selectChange = (index: number, checked: boolean) => {
    setCartCanBuyList((prev) =>
      prev.map((item, i) => (i === index ? { ...item, selected: checked } : item)),
    );
    // 编辑态勾选不上报 CartitemBtn_Click
    if (cartActions.batchFlag) return;
    // 需要等到拿到计价结果上报最新的结果，所以加了一个延迟
    setTimeout(() => {
      const group = cartCanBuyListRef.current[index];
      const btnName = checked ? 'item选择' : 'item取消';
      const cart = group?.carts?.[0];

      if (cart) {
        const cartId = cart.cart_id ?? 0;
        trackCartItemBtnClick(cart as any, btnName, {
          selected: checked,
          itemPromotion: promotionRef.current.find((item) => item.id === cartId) || null,
        });
      }
    }, 2000);
  };

  // 使用 cartActions 中的方法
  const moveWish = cartActions.handleBatchCollect;
  const handleMoveToWish = cartActions.handleBatchCollect;
  const { handleBatchDelete } = cartActions;
  const { batchCancel } = cartActions;
  const clearAll = cartActions.handleClearAll;

  // 构造埋点数据的辅助函数
  const buildTrackingData = () => {
    const selectedCartItems = selectedCanBuy.map((item) => ({
      cart_id: item.cart_id,
      shop_sku: item.product?.shop_sku || '',
      business_sku: (item.product as any)?.business_sku || '',
      product_sku: item.product?.shop_sku || '', // 使用shop_sku作为product_sku
      product_id: item.product_id,
      product_name: item.product?.short_name || '',
      qty: item.qty,
      product: item.product,
      // 包含处方相关信息
      option: item.option,
      lens: item.lens,
      coating: item.coating,
    }));

    const priceData = {
      total: totalData.total || '0',
      subtotal: totalData.subtotal || '0',
      discount: totalData.discount || '0',
      tmp: totalData.tmp,
    };

    return { selectedCartItems, priceData };
  };

  // 结算
  const checkout = async () => {
    if (selectedCanBuy.length === 0) {
      // 🔍 发送神策埋点 - 结算失败（没有选中商品）
      try {
        const priceData = {
          total: totalData.total || '0',
          subtotal: totalData.subtotal || '0',
          discount: totalData.discount || '0',
          tmp: totalData.tmp,
        };

        trackCheckoutResult([] as any, priceData, false, '0001', 'no_product');
        // 没有选中商品，所以不需要发送商品详细结果埋点
      } catch (trackingError) {
        logger.error('Failed to send checkout result tracking (no items selected):', trackingError);
      }
      toast.info(t('pleaseSelectItems'));
      return;
    }

    // 🔍 发送神策埋点 - 结算按钮点击
    try {
      const { selectedCartItems, priceData } = buildTrackingData();

      trackCheckoutClickForThirdParty(selectedCartItems as any, priceData);
    } catch (trackingError) {
      logger.error('Failed to send checkout click tracking:', trackingError);
    }
    setCheckoutLoading(true);
    try {
      // 检查小计是否为0.00
      if (totalData.subtotal === '0.00') {
        let allow = true;
        for (let i = 0; i < selectedCanBuy.length; i++) {
          if (selectedCanBuy[i].type !== 4 && selectedCanBuy[i].type !== 5) {
            allow = false;
            break;
          }
        }
        if (!allow) {
          // 🔍 发送神策埋点 - 结算失败（小计为0）
          try {
            const { selectedCartItems, priceData } = buildTrackingData();
            trackCheckoutResult(
              selectedCartItems as any,
              priceData,
              false,
              '0002',
              'amount_less_than_or_equal_to_zero',
            );
            // 为每个商品发送详细结果埋点
            selectedCartItems.forEach((item) => {
              trackCheckoutProductDetailResult(
                item as any,
                false,
                '0002',
                0,
                'amount_less_than_or_equal_to_zero',
                {
                  selected: true,
                  itemPromotion: getItemPromotion((item as any).cart_id || 0),
                },
              );
            });
          } catch (trackingError) {
            logger.error('Failed to send checkout result tracking:', trackingError);
          }

          toast.error(t('subtotalZeroError'));
          return;
        }
      }

      // 检查登录状态
      if (!token) {
        // 🔍 发送神策埋点 - 结算失败（用户未登录）
        try {
          const { selectedCartItems, priceData } = buildTrackingData();
          trackCheckoutResult(selectedCartItems as any, priceData, false, '0401', 'no_login');
          // 为每个商品发送详细结果埋点
          selectedCartItems.forEach((item) => {
            trackCheckoutProductDetailResult(item as any, false, '0401', 0, 'no_login', {
              selected: true,
              itemPromotion: getItemPromotion((item as any).cart_id || 0),
            });
          });
        } catch (trackingError) {
          logger.error('Failed to send checkout result tracking (not logged in):', trackingError);
        }

        // 未登录，标记是通过checkout触发的登录，保存当前选中商品状态
        setClickCheckOut(true);
        setNeedFastRegisterCoupon(false);
        setPrelandingCartList([...cartCanBuyList]);
        setPrelandingSelectedCanBuy([...selectedCanBuy]);
        const { code, data } = await getResourceData({
          info: [
            {
              key: 'cart_checkout_sign_up_test_m_pc',
            },
          ],
        });
        // 结算触发常规注册登录弹窗
        if (code === 200 && data?.info?.cart_checkout_sign_up_test_m_pc === 2) {
          setShowLoginRegisterDialog(true);
          return;
        }
        // 结算触发快捷注册登录弹窗
        setQuickCouponDialog({
          show: true,
          eventType: 'shopping_cart_checkout',
          loginSituation: '购物车结算',
          triggerType: 'auto',
        });
        return;
      }

      // 已登录，设置选中的购物车ID到store中，用于payment页商品展示
      setSelectedCartIds(allSelectedCartIds);

      // 调用支付前校验接口
      const { data, code } = await checkCartBeforeCheckout({ cart_ids: allSelectedCartIds });

      if (code === 200 && data.code) {
        // 有限制，不能跳转结算页
        // 🔍 发送神策埋点 - 结算失败（校验失败）
        try {
          const { selectedCartItems, priceData } = buildTrackingData();
          trackCheckoutResult(
            selectedCartItems as any,
            priceData,
            false,
            String(data.code),
            data.content,
          );
          // 为每个商品发送详细结果埋点
          selectedCartItems.forEach((item) => {
            trackCheckoutProductDetailResult(
              item as any,
              false,
              String(data.code),
              0,
              data.content,
              {
                selected: true,
                itemPromotion: getItemPromotion((item as any).cart_id || 0),
              },
            );
          });
        } catch (trackingError) {
          logger.error('Failed to send checkout result tracking:', trackingError);
        }

        handleErrorCode(data);
      } else if (code === 200) {
        // 校验通过，跳转到结算页面
        // 🔍 发送神策埋点 - 结算成功
        try {
          const { selectedCartItems, priceData } = buildTrackingData();
          trackCheckoutResult(selectedCartItems as any, priceData, true, '200', 'success');
          // 为每个商品发送详细结果埋点 - 需要计算每个商品的实际金额
          selectedCartItems.forEach((item) => {
            // 可以从totalData.tmp.carts中找到对应商品的价格信息 - 使用cart_id匹配
            const cartPriceItem = totalData.tmp?.carts?.find(
              (cart: any) => cart.id === (item as any).cart_id,
            );
            const productAmount = cartPriceItem
              ? parseFloat(cartPriceItem.product_payable || '0') +
                parseFloat(cartPriceItem.product_rule_discounts_price || '0')
              : 0;

            trackCheckoutProductDetailResult(item as any, true, '200', productAmount, 'success', {
              selected: true,
              itemPromotion: getItemPromotion((item as any).cart_id || 0),
            });
          });
        } catch (trackingError) {
          logger.error('Failed to send checkout result tracking:', trackingError);
        }

        sessionStorage.setItem('zl-sensors-entrance-payment-scene', '购物车');
        router.push('/payment');
      }
    } catch (error) {
      logger.error('Checkout failed:', error);
      toast.error(t('checkoutFailed'));
    } finally {
      setCheckoutLoading(false);
    }
  };

  // 处理错误码
  const handleErrorCode = (data: any) => {
    if (Number(data.code) === 400880) {
      // 特殊处理弹窗 - 过期商品
      setCannotCheckoutData({
        title: data.title,
        list: [
          {
            content: data.content,
            products: data.products,
          },
        ],
        show: true,
        confirmBtnText: 'OK',
        onClose: () => {
          reloadCart();
        },
      });
    } else {
      // 展示限购弹窗
      setCannotSubmitData({
        show: true,
        title: data.title,
        content: data.content,
        code: data.code,
        products: data.products || [],
        onClose: () => {
          reloadCart();
        },
      });
    }
  };

  // 返回
  const goBack = () => {
    // 检查URL参数中是否有from=payment
    const urlParams = new URLSearchParams(window.location.search);
    const fromParam = urlParams.get('from');

    if (fromParam === 'payment') {
      // 如果来自payment页面，跳转到首页
      router.push('/');
    } else {
      // 否则返回上一页
      router.back();
    }
  };

  // 防重复调用的ref - 更强力的防抖机制
  const pendingCalculationRef = useRef<boolean>(false);
  const lastCalculationParamsRef = useRef<string>('');

  // 依赖项变化追踪
  const lastDepsRef = useRef<any>({});

  // 计价触发的useEffect - 直接在useEffect中处理逻辑，避免函数依赖
  useEffectWithCurReady(() => {
    // 检查具体哪个依赖项发生了变化
    const currentDeps = {
      selectedCartIdStr,
      selectedCartDataHash,
      isCompleted,
      allSelectedCartIds: JSON.stringify(allSelectedCartIds),
      currencyName: currency?.name,
      selectedCanBuy: JSON.stringify(
        selectedCanBuy?.map((item) => ({ cart_id: item.cart_id, qty: item.qty })),
      ),
    };

    lastDepsRef.current = currentDeps;

    // 只有在初始化完成后才进行价格计算和包邮政策查询
    if (!isCompleted) {
      return;
    }

    // WAP端批量模式下不触发计价
    if (deviceType === EPlatform.WAP && cartActions.batchFlag) {
      // 保证loading状态可以关闭
      setCheckoutLoading(false);
      return;
    }

    // 生成当前调用的参数标识
    const currentParams = `${selectedCartIdStr}-${selectedCartDataHash}-${currency?.name}`;

    // 强力防重复机制：
    // 1. 如果参数相同，跳过
    // 2. 如果有计算正在进行，跳过

    if (lastCalculationParamsRef.current === currentParams) {
      if (!pendingCalculationRef.current) {
        setCheckoutLoading(false);
      }
      return;
    }

    // 在return出去前记录当前参数
    lastCalculationParamsRef.current = currentParams;
    setCheckoutLoading(true);

    if (pendingCalculationRef.current) {
      return;
    }
    // 更新状态和参数
    pendingCalculationRef.current = true;

    // 定义计价逻辑（在useEffect内部，避免函数依赖问题）
    const executeCalculation = async () => {
      const targetParams = lastCalculationParamsRef.current;
      const currentSelectedItems = selectedCanBuyRef.current || [];
      const currentIds = currentSelectedItems.map((item) => item.cart_id).filter(Boolean);

      // 获取包邮政策的内部函数
      const fetchFreeShipping = async (cartIds: number[]) => {
        try {
          if (!currency?.name) {
            logger.warn('Currency not ready, skipping getFreeShippingPolicy call');
            return;
          }
          const response = await getFreeShippingPolicy({
            currency: currency.name,
            cart_ids: cartIds || [],
          });
          if (response?.data) {
            setFreeShipData(response.data);
          } else {
            setFreeShipData({});
          }
        } catch (error) {
          logger.log('error', error);
          setFreeShipData({});
        }
      };

      try {
        if (currentIds.length > 0) {
          // 并行获取价格数据和包邮政策
          try {
            const [priceResponse] = await Promise.all([
              getSubTotalPrice({ cart_ids: currentIds, currency: currency?.name }),
              fetchFreeShipping(currentIds),
            ]);

            if (priceResponse?.data) {
              setTotalData({
                total: priceResponse.data.total || '0.00',
                discount: priceResponse.data.discount || '0.00',
                subtotal: priceResponse.data.subtotal || '0.00',
                tmp: priceResponse.data.tmp,
                show_gift: priceResponse.data.show_gift,
                base_checked_product_amount: priceResponse.data.base_checked_product_amount,
                base_checked_subtotal_product_amount:
                  priceResponse.data.base_checked_subtotal_product_amount,
              });

              // 更新促销数据
              if (priceResponse.data.tmp?.carts) {
                setPromotion(priceResponse.data.tmp.carts);
              } else {
                setPromotion([]);
              }

              try {
                // 发送conversion埋点
                trackConversion(
                  priceResponse.data.subtotal,
                  currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
                );

                // 发送add_cart埋点
                trackAddCart(currentSelectedItems as any);

                // 🔍 第三方埋点 - Emarsys购物车追踪
                trackEmarsysCart(priceResponse.data);
              } catch (trackingError) {
                logger.error('Failed to send GA conversion tracking:', trackingError);
              }
            }
          } catch (error) {
            logger.error('Failed to get cart price or free shipping:', error);
          }
        } else {
          setTotalData({
            total: '0.00',
            discount: '0.00',
            subtotal: '0.00',
            base_checked_product_amount: '0.00',
            base_checked_subtotal_product_amount: '0.00',
          });
          setPromotion([]);
          // 没有选中商品时也要调用包邮政策接口
          await fetchFreeShipping([]);
        }
      } finally {
        // 使用分叉判断进行【递归检查】，防止checkout按钮闪烁
        if (targetParams !== lastCalculationParamsRef.current) {
          // 不相等，说明在请求期间，参数又变了
          // 保持 Loading，保持 Pending，直接递归再算一次
          await executeCalculation();
        } else {
          // 相等，说明已经追上了用户的最新操作。
          // 无论如何都会走到这步然后解锁
          pendingCalculationRef.current = false;
          setCheckoutLoading(false);
          setHasPricingSettled(true);
        }
      }
    };
    executeCalculation();
  }, [
    // 核心计价依赖：只有这些变化时才重新计价
    selectedCartIdStr, // 选中商品ID变化
    selectedCartDataHash, // 选中商品数据变化（数量、配置等）
    currency?.name, // 币种变化
    isCompleted, // 初始化完成状态
    deviceType, // 设备类型变化
    cartActions.batchFlag, // 批量模式状态变化
  ]);

  // 获取优惠券提示
  const getDetailCouponTips = useCallback(async () => {
    if (Object.keys(couponData).length === 0) {
      try {
        const { data } = await fetchBffDetailCouponTips();
        if (data) {
          setCouponData(data);
        }
      } catch (error) {
        logger.log(error);
      }
    }
  }, [couponData]);

  // 获取可用优惠券列表
  const getOrderCouponData = useCallback(async () => {
    try {
      if (token && selectedCanBuy.length) {
        const params = {
          cart_ids: selectedCanBuy.map((item: any) => item.cart_id),
          currency: currency.name,
        };
        const { data } = await getOrderCoupon(params);
        if (data.list) {
          setCouponInfo(data);
        } else {
          setCouponInfo(null);
        }
      } else {
        setCouponInfo(null);
      }
    } catch (error) {
      setCouponInfo(null);
      logger.log(error);
    }
    getDetailCouponTips();
  }, [token, selectedCartIdStr, currency.name, getDetailCouponTips]);

  // 监听优惠券相关数据变化
  useEffect(() => {
    // 只有在初始化完成后才调用相关接口
    if (isCompleted) {
      // WAP端批量模式下不触发优惠券接口调用
      if (deviceType === EPlatform.WAP && cartActions.batchFlag) {
        return;
      }

      // 已登录且有选中商品时调用获取可用优惠券列表接口
      if (selectedCartIdStr && token) {
        getOrderCouponData();
      } else {
        // 未登录或没有选中商品时，只调用优惠券提示接口
        getDetailCouponTips();
      }
    }
  }, [selectedCartIdStr, initState, deviceType, cartActions.batchFlag]);

  // 优惠券相关计算逻辑
  const showCouponTip = useMemo(() => {
    // 用户有优惠券且已登录
    if (couponInfo?.list?.length && token) {
      // 过滤满足条件的优惠券
      const couponList = couponInfo.list.filter((d: any) => {
        const { discount_code: { way, full_discounts_details = {} } = {} } = d;
        const { discounts_type, discounts_value } = full_discounts_details || {};
        // 移除金额中的逗号
        const money = String(totalData.subtotal).replace(/,/g, '');
        const disValueNoComma = discounts_value ? String(discounts_value).replace(/,/g, '') : 0;
        return way === 2 && discounts_type === 1 && Number(disValueNoComma) <= Number(money);
      });

      if (couponList.length) {
        let maxSaving = couponList[0];
        couponList.forEach((d: any) => {
          const {
            discount_code: { full_discounts_details: { discounts_value = '' } = {} },
          } = d;
          maxSaving =
            Number(maxSaving.discount_code.full_discounts_details.discounts_value) >
            Number(discounts_value)
              ? maxSaving
              : d;
        });
        return {
          value: Number(maxSaving.discount_code.full_discounts_details.discounts_value).toFixed(2),
        };
      }
    }
    return null;
  }, [couponInfo, token, totalData.subtotal]);

  // 计算最大节省信息
  const maxSaving = useMemo(() => {
    if (token) {
      if (showCouponTip) {
        return `<span class="notranslate">${formatPriceNumberWithSymbol(`${showCouponTip.value}`, currency)}</span> | ${t('maxSaving')}`;
      } else {
        return t('applyCouponCodePoints');
      }
    } else if (!token && Object.keys(couponData).length) {
      if (couponData.way === 1) {
        return `<span class="notranslate">${couponData.discounts_details?.discounts_value}% ${t('off')} </span> ${t('forFirstOrder')}`;
      } else if (couponData.way === 2) {
        return `<span class="notranslate">${formatPriceNumberWithSymbol(`${couponData.full_discounts_details.discounts_value}`, currency)} ${t('off')} </span> ${t('forFirstOrder')}`;
      }
    }
    return '';
  }, [token, showCouponTip, couponData]);

  useEffect(() => {
    if (canNotBuyList.length > 0) {
      // 处理不可购买列表
      const processedList = canNotBuyList.map((item) => ({
        ...item,
        selected: false,
      }));
      setCartCanNotBuyList(processedList);
    } else {
      setCartCanNotBuyList([]);
    }
  }, [canNotBuyList]);

  // 更新全选状态
  useEffect(() => {
    if (cartActions.batchFlag) {
      // 编辑模式：需要同时考虑可结算和不可结算商品
      const hasSelectableCanBuyItems = cartCanBuyList.some((item) => !item.disabled);
      const hasCanNotBuyItems = cartCanNotBuyList.length > 0;

      const canBuyAllSelected = hasSelectableCanBuyItems
        ? cartCanBuyList.every((item) => item.disabled || item.selected)
        : true;
      const canNotBuyAllSelected = hasCanNotBuyItems
        ? cartCanNotBuyList.every((item) => item.selected)
        : true;

      const allSelected =
        (hasSelectableCanBuyItems || hasCanNotBuyItems) &&
        canBuyAllSelected &&
        canNotBuyAllSelected;
      setAllCheck(allSelected);
    } else {
      // 普通模式：只考虑可结算商品
      const hasSelectableItems = cartCanBuyList.some((item) => !item.disabled);
      const allSelected =
        hasSelectableItems && cartCanBuyList.every((item) => item.disabled || item.selected);
      setAllCheck(allSelected);
    }
  }, [cartCanBuyList, cartCanNotBuyList, cartActions.batchFlag]);

  // 避免重复初始化
  const hasInitializedRef = useRef(false);

  // 初始化数据
  useEffectWithCurReady(() => {
    // 只执行一次
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    const initializeData = async () => {
      try {
        setIsGlobalLoading(true);
        const { canBuyList: currentCanBuyList, canBuyCartsList: currentCanBuyCartsList } =
          await setCart();
        setIsInitData(true);

        // 初始化购物车快照
        const initialSnapshot = initializeCartSnapshot(currentCanBuyList || []);

        // 初始化默认数据
        initializeDefaultData();
        // 处理初始化的勾选状态 (对应Vue项目的mounted逻辑)
        // 注意：此时canBuyList和canBuyCartsList已经通过setCart()更新了
        setInitState('processing');
        // 使用store中的最新数据，确保数据一致性，并传递快照数据避免时序问题
        const handledList = handleMountedCheck(
          currentCanBuyList || [],
          currentCanBuyCartsList || [],
          initialSnapshot,
        );
        setCartCanBuyList(handledList);
        setIsGlobalLoading(false);
        await refreshCartData();

        // 延迟设置初始化完成标记，确保选中状态稳定后再允许用户操作保存
        setTimeout(() => {
          setInitState('completed');
        }, 100); // 100ms延迟，确保UI状态稳定
      } catch (error) {
        logger.error('Failed to initialize cart data:', error);
      } finally {
        setIsFirstIn(false);
      }
    };
    initializeData();
  }, [setCart, initializeDefaultData]);

  // 🔍 神策埋点 - Cart_View：等购物车首屏数据就绪后再上报，避免拿不到购物车数据就上报，导致 product_quantity 仍为 0
  //  问题case: https://alidocs.dingtalk.com/i/nodes/KGZLxjv9VG3jON9ZfY7NMGz9V6EDybno?utm_scene=team_space
  useEffect(() => {
    if (!isInitData || !isCompleted || !hasPricingSettled) {
      return;
    }
    // 有商品时等 cartCanBuyList 与 store 同步后再上报，避免 cart_item_info_list 为空
    const hasStoreLines = canBuyList?.some((g) => (g.carts?.length ?? 0) > 0);
    if (hasStoreLines && cartCanBuyList.length === 0) {
      return;
    }
    const checkedProductQuantity = selectedCanBuy.reduce((sum, item) => sum + (item.qty || 0), 0);
    trackCartView(
      checkedProductQuantity,
      getItemPromotion,
      cartCanBuyList,
      totalData.base_checked_product_amount,
      totalData.base_checked_subtotal_product_amount,
    );
  }, [
    isInitData,
    isCompleted,
    hasPricingSettled,
    deviceType,
    cartActions.batchFlag,
    trackCartView,
    selectedCanBuy,
    cartCanBuyList,
    canBuyList,
    getItemPromotion,
    totalData.base_checked_product_amount,
    totalData.base_checked_subtotal_product_amount,
  ]);

  // 🔍 第三方埋点 - 购物车页面初始化埋点
  useEffect(() => {
    // 确保数据真正加载完成，避免空数组问题
    if (isCompleted && canBuyList && canBuyList.length > 0) {
      // 延迟执行，确保store数据已经传播到组件
      const timer = setTimeout(() => {
        try {
          // Criteo viewBasket埋点 - 使用所有可购买商品（对应Vue版本的canBuyList）
          const allCartItems = canBuyList.flatMap((group) => group.carts || []);
          if (allCartItems.length > 0) {
            trackCriteoViewBasket(allCartItems as any);
          }
          // Uetq (Bing)购物车浏览埋点
          trackUetqCart(canBuyList.map((group) => group.carts || []) as any);
        } catch (error) {
          logger.error('第三方埋点发送失败:', error);
        }
      }, 200); // 延迟确保数据稳定

      return () => clearTimeout(timer);
    }
  }, [isCompleted, canBuyList, trackCriteoViewBasket, trackUetqCart]);

  // 🔍 神策埋点 - Cart_Leave：页面离开埋点
  useEffect(() => {
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    return () => {
      trackCartLeave({ refer_page_name, refer_url_path });
    };
  }, []);

  // 记录token的初始状态，避免页面刷新时触发登录逻辑
  useEffect(() => {
    if (!hasTokenInitialized) {
      initialTokenRef.current = token;
      setHasTokenInitialized(true);
    }
  }, [token, hasTokenInitialized]);

  // 监听用户登录状态变化，新增checkout登录处理
  useEffect(() => {
    // 如果token还未初始化，跳过处理
    if (!hasTokenInitialized) {
      return;
    }

    // 检查token是否真正发生了变化（从无到有，或从有到无）
    const tokenChanged = initialTokenRef.current !== token;
    if (!tokenChanged) {
      return;
    }

    // 更新token记录
    initialTokenRef.current = token;

    const handleUserLogin = async () => {
      // 检查是否是通过checkout触发的登录
      if (token && clickCheckOut) {
        try {
          setClickCheckOut(false); // 重置checkout标记
          setNeedFastRegisterCoupon(true);
          // 执行checkout成功逻辑
          await checkoutSuccess();
        } catch (error) {
          logger.error('Checkout login handling failed:', error);
          setClickCheckOut(false); // 出错时也要重置标记
          setNeedFastRegisterCoupon(true);
        }
        return; // checkout登录流程结束，不执行普通登录逻辑
      }

      // 普通登录流程：只有在非首次进入且用户已登录且未处理过登录逻辑时才执行刷新逻辑
      if (token && !isFirstIn && isInitData && !hasProcessedLogin) {
        try {
          // 标记已处理登录逻辑，防止重复执行
          setHasProcessedLogin(true);

          // 调用 reloadCart 确保购物车合并逻辑正确执行
          if (reloadCartRef.current) {
            await reloadCartRef.current();
          }
          // 强制推荐组件重新渲染，触发重新请求推荐数据
          setRecommendKey((prev) => prev + 1);
        } catch (error) {
          logger.error('Failed to refresh cart after login:', error);
          // 出错时重置标记，允许重试
          setHasProcessedLogin(false);
        }
      }
    };

    // 延迟执行，避免与其他 useEffect 冲突
    const timer = setTimeout(handleUserLogin, 0);
    return () => clearTimeout(timer);
  }, [
    token,
    hasTokenInitialized,
    isFirstIn,
    isInitData,
    hasProcessedLogin,
    clickCheckOut,
    checkoutSuccess,
  ]);

  // 重置登录处理标记（当用户退出登录时）
  useEffect(() => {
    if (!token && hasTokenInitialized) {
      setHasProcessedLogin(false);
    }
  }, [token, hasTokenInitialized]);

  // 使用 useCartState 提供的 handleInvalidSelectChange
  const handleInvalidSelectChange = cartStateHandleInvalidSelectChange;

  // 处理广告位点击埋点
  const handleCartPageBannerClick = useCallback(() => {
    if (advertObj && advertObj.jump_type === 2) {
      setSession('zl_goodsDetailEnterPageScene', '购物车页广告位');
    }
    trackCartPageBannerClick(advertObj);
  }, [trackCartPageBannerClick, advertObj]);

  // 处理广告位曝光埋点
  const handleCartPageBannerExp = useCallback(() => {
    trackCartPageBannerExp(advertObj);
  }, [trackCartPageBannerExp, advertObj]);

  if (!isInitData) {
    // 数据未初始化完成时，显示最小高度容器，因为pageLoading会显示loading
    return <div className="w-full min-h-screen bg-[#f5f6f7] relative pb-[80px]" />;
  }

  return (
    <div className="w-full min-h-screen bg-[#ffffff] relative pb-[80px] md:pb-[0px]">
      {/* 根据设备类型渲染对应的组件 */}
      {deviceType === EPlatform.WAP ? (
        <CartPageWAP
          cartCanBuyList={cartCanBuyList}
          cartCanNotBuyList={cartCanNotBuyList}
          allCheck={allCheck}
          batchFlag={cartActions.batchFlag}
          batchDisabled={cartActions.batchDisabled}
          checkoutLoading={checkoutLoading}
          showMoreInvalid={showMoreInvalid}
          totalData={totalData}
          allQty={allQty}
          selectedCanBuy={selectedCanBuy}
          currency={currency}
          halfPriceConfig={halfPriceConfig}
          maxSaving={maxSaving}
          showAdsImg={showAdsImg}
          advertObj={advertObj}
          advertData={advertData}
          recommendList={recommendList}
          freeShipData={freeShipData}
          onGoBack={goBack}
          onCheckChange={checkChange}
          onBatchFlagChange={cartActions.setBatchFlag}
          onSelectChange={selectChange}
          onInvalidSelectChange={handleInvalidSelectChange}
          onReloadCart={reloadCart}
          onBatchCancel={batchCancel}
          onClearAll={clearAll}
          onCheckout={checkout}
          onShowMoreInvalidChange={setShowMoreInvalid}
          onMoveToWish={handleMoveToWish}
          onBatchDelete={handleBatchDelete}
          onCartPageBannerClick={handleCartPageBannerClick}
          onCartPageBannerExp={handleCartPageBannerExp}
          getItemPromotion={getItemPromotion}
          swiperManager={swiperManager}
        />
      ) : (
        <CartPagePC
          cartCanBuyList={cartCanBuyList}
          cartCanNotBuyList={cartCanNotBuyList}
          allCheck={allCheck}
          batchFlag={cartActions.batchFlag}
          batchDisabled={cartActions.batchDisabled}
          checkoutLoading={checkoutLoading}
          showMoreInvalid={showMoreInvalid}
          totalData={totalData}
          allQty={allQty}
          selectedCanBuy={selectedCanBuy}
          currency={currency}
          halfPriceConfig={halfPriceConfig}
          maxSaving={maxSaving}
          showAdsImg={showAdsImg}
          advertObj={advertObj}
          advertData={advertData}
          recommendList={recommendList}
          freeShipData={freeShipData}
          onCheckChange={checkChange}
          onBatchFlagChange={cartActions.setBatchFlag}
          onSelectChange={selectChange}
          onInvalidSelectChange={handleInvalidSelectChange}
          onReloadCart={reloadCart}
          onMoveWish={moveWish}
          onBatDelete={handleBatchDelete}
          onBatchCancel={batchCancel}
          onClearAll={clearAll}
          onCheckout={checkout}
          onShowMoreInvalidChange={setShowMoreInvalid}
          onCartPageBannerClick={handleCartPageBannerClick}
          onCartPageBannerExp={handleCartPageBannerExp}
          getItemPromotion={getItemPromotion}
        />
      )}
      {/* 空购物车状态 */}
      {cartCanBuyList.length === 0 && cartCanNotBuyList.length === 0 && (
        <CartEmpty deviceType={deviceType} />
      )}
      {/* 推荐商品（PC和移动端共用） */}
      <YouMightLikeWaterFall
        key={`cart-recommend-${recommendKey}`} // 使用 key 强制重新渲染，触发推荐数据重新请求
        recommendList={[]} // 传递空数组，让组件自己加载数据
        deviceType={deviceType}
        onAdvertDataLoaded={(advertItem, recommendData) => {
          setAdvertData(advertItem);
          setRecommendList(recommendData);
        }}
      />
      {/* 确认对话框 */}
      <CartItemDialog
        open={cartDialogs.showCartItemDialog}
        onClose={() => cartDialogs.setShowCartItemDialog(false)}
        {...cartDialogs.cartItemDialogProps}
      />

      {/* 第二件半价弹窗 */}
      <HalfPriceDialog
        selectedCanBuy={selectedCanBuy}
        batchFlag={cartActions.batchFlag}
        currency={currency}
        publicHalfPrice={halfPriceConfig}
      />
    </div>
  );
}
